/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import styled from "styled-components";
import Text from "./ui/Text";
import Form from "./ui/form/Form";
import FormGroup from "./ui/form/FormGroup";
import FormFieldsWrapper from "./ui/form/FormFieldsWrapper";
import { useForm } from "react-hook-form";
import FormError from "./ui/form/FormError";
import Button from "./ui/Button";
import EmailField from "./ui/form/EmailField";
import TextField from "./ui/form/TextField";
import { useGenerateGdprCode } from "../lib/react-query/useGenerateGdprCode";
import { useClientPersonalData } from "../lib/react-query/useClientPersonalData";
import ButtonGroup from "./ui/ButtonGroup";
import T from "./ui/Translation";
import { useAuth } from "../hooks/useAuth";
import { extractErrorMessage } from "../utils/api/apiErrors";

const StyledPersonalDataRequestFormWrapper = styled.div`
  padding: 0.5rem 1rem;
`;

const StyledTitle = styled((props) => <Text {...props} />)`
  && {
    margin-bottom: 8px;
  }
`;

const StyledPersonalDataRequestForm = styled((props) => <Form {...props} />)`
  margin-top: 16px;
`;

const PersonalDataRequestForm = () => {
  const { client } = useAuth();
  const [email, setEmail] = useState(client?.email);
  const [step, setStep] = useState(1);
  const { mutate: getGdprCode, isPending: isGdprCodeLoading } = useGenerateGdprCode();
  const { mutate: getPersonalData, isPending: isPersonalDataLoading } = useClientPersonalData();
  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm();

  const onSubmitEmail = async (data) => {
    getGdprCode(data.email, {
      onSuccess: () => {
        setEmail(data.email);
        setStep(2);
      },
      onError: (error) => {
        setError("email", { message: extractErrorMessage(error, "Something went wrong") });
      },
    });
  };

  const onSubmitCode = async (data) => {
    getPersonalData(
      { code: data.code, email },
      {
        onError: (error) => {
          setError("code", { message: extractErrorMessage(error, "Something went wrong") });
        },
      }
    );
  };

  const handleSendAgain = () => {
    onSubmitEmail({ email });
  };

  return (
    <StyledPersonalDataRequestFormWrapper>
      <StyledTitle color="dark" weight="bold">
        Get your GDPR information
      </StyledTitle>
      {step === 1 && (
        <>
          <Text color="dark">
            1. <T>Enter email/phone whith you used during bookings.</T>
          </Text>
          <Text color="dark">
            2. <T>Enter code sent to your email/phone.</T>
          </Text>
          <StyledPersonalDataRequestForm onSubmit={handleSubmit(onSubmitEmail)}>
            <FormFieldsWrapper>
              <FormGroup>
                <EmailField control={control} name="email" label="Email or phone" defaultValue={email} required />
                {errors.email && <FormError name="email">{errors.email.message}</FormError>}
              </FormGroup>
            </FormFieldsWrapper>
            <Button variant="primary" title="Send code" loading={isGdprCodeLoading} />
          </StyledPersonalDataRequestForm>
        </>
      )}
      {step === 2 && (
        <>
          <Text color="dark">Enter code sent to your email/phone.</Text>
          <StyledPersonalDataRequestForm onSubmit={handleSubmit(onSubmitCode)}>
            <FormFieldsWrapper>
              <FormGroup>
                <TextField control={control} name="code" label="Code" required />
                {errors.code && <FormError name="code">{errors.code.message}</FormError>}
              </FormGroup>
            </FormFieldsWrapper>
            <ButtonGroup displayAs="column" itemsWidth="full">
              <Button
                variant="primary"
                title="Get report"
                loading={isPersonalDataLoading}
                disabled={isGdprCodeLoading}
              />
              <Button
                variant="transparent"
                title="send again"
                type="button"
                onClick={handleSendAgain}
                disabled={isGdprCodeLoading}
              />
            </ButtonGroup>
          </StyledPersonalDataRequestForm>
        </>
      )}
    </StyledPersonalDataRequestFormWrapper>
  );
};

export default PersonalDataRequestForm;
